const mediaQueryLarge = window.matchMedia('(min-width: 1025px)');
function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}
class SidebarAccordion extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  get #navigateWithButtons() {
    return this.dataset.navigateWithButtons === 'true';
  }

  get #hasLinks() {
    return this.details.querySelector('.details-content') !== null;
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    window.removeEventListener('scroll', this.handleWindowScroll);
  }

  handleWindowScroll = () => {
    const isMobile = isMobileBreakpoint();
    if (this.details.open && isMobile) {
      this.style.setProperty(
        '--sidebar-accordion-offset-top',
        `${this.getBoundingClientRect().top}px`
      );
    }
  };

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if (isMobile && !this.details.open) {
      this.style.setProperty(
        '--sidebar-accordion-offset-top',
        `${this.getBoundingClientRect().top}px`
      );
      window.addEventListener('scroll', this.handleWindowScroll);
    }

    if (this.details.open) {
      window.removeEventListener('scroll', this.handleWindowScroll);
    }

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile &&
        this.hasAttribute('open-by-default-on-mobile') &&
        this.getAttribute('open-by-default-on-mobile') !== 'false') ||
      (!isMobile &&
        this.hasAttribute('open-by-default-on-desktop') &&
        this.getAttribute('open-by-default-on-desktop') !== 'false');
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    const currentTarget = event.currentTarget;
    switch (event.key) {
      case 'Escape':
        if (!this.#closeWithEscape) {
          return;
        }
        event.preventDefault();
        this.details.open = false;
        this.summary.focus();
        break;
      case 'ArrowRight':
        if (!this.details.open) {
          event.preventDefault();
          this.details.open = true;
          this.summary.focus();
        }
        break;
      case 'ArrowLeft':
        if (!this.#closeWithEscape) {
          return;
        }
        event.preventDefault();
        this.details.open = false;
        this.summary.focus();

        break;
      case 'ArrowDown':
        if (!this.#navigateWithButtons) {
          return;
        }
        event.preventDefault();
        this.#verticalNavigation('down');

        break;
      case 'ArrowUp':
        if (!this.#navigateWithButtons) {
          return;
        }
        event.preventDefault();
        this.#verticalNavigation('up');

        break;
    }
  }

  /**
   *
   * @param {'up' | 'down'} direction
   */
  #verticalNavigation(direction) {
    const isChildNavigation = this.details.open && this.#hasLinks;
    const activeItem = document.activeElement;
    let contentLinks;
    if (isChildNavigation) {
      contentLinks = [...this.details.querySelectorAll('.details-content .sidebar-link')];
    } else {
      const parentDetails = this.details.closest('.details-content');
      if (!(parentDetails instanceof HTMLElement))
        throw new Error('Parent details content not found');
      contentLinks = [...parentDetails.querySelectorAll('summary')];
    }

    const currentIndex = activeItem ? contentLinks.indexOf(activeItem) : 0;

    const nextLink =
      direction === 'up'
        ? contentLinks[currentIndex - 1] || contentLinks[contentLinks.length - 1]
        : contentLinks[currentIndex + 1] || contentLinks[0];
    if (!(nextLink instanceof HTMLElement)) throw new Error('previousLink element not found');
    nextLink.focus();
  }
}

if (!customElements.get('sidebar-accordion')) {
  customElements.define('sidebar-accordion', SidebarAccordion);
}
